/**
 * The draft a team form holds, and the two pure conversions between it and the
 * wire. Extracted from the route so both directions can be asserted directly:
 * `api.post` and `api.put` type their body as `unknown`, so nothing about the
 * shape actually sent is provable from the call site.
 *
 * Every field is a string, because that is what a form control holds. `season`
 * is kept in its `seasonToValue` encoding ("2026-fall") — the same string the
 * picker uses as its `<option value>`.
 */
import { seasonFromValue, seasonToValue } from "@alpha/shared";
import type { Team } from "./types";

export interface TeamDraft {
  name: string;
  city: string;
  state: string;
  ageGroup: string;
  classification: string;
  /** `seasonToValue` encoding; "" when nothing is picked. */
  season: string;
}

export const emptyDraft = (): TeamDraft => ({
  name: "",
  city: "",
  state: "",
  ageGroup: "",
  classification: "",
  season: "",
});

/**
 * Entity -> draft. Every field is defaulted even though two of them are typed
 * required: a team stored before the attributes existed reaches the client
 * through an unvalidated cast, and a select bound to `undefined` silently
 * becomes an uncontrolled one. An empty control beats the string "undefined".
 */
export function toDraft(team: Team): TeamDraft {
  return {
    name: team.name ?? "",
    city: team.city ?? "",
    state: team.state ?? "",
    ageGroup: team.ageGroup ?? "",
    classification: team.classification ?? "",
    season: team.season ? seasonToValue(team.season) : "",
  };
}

/**
 * Draft -> request body. Blank optionals are dropped rather than sent empty,
 * the way `AdminVenues.toBody` drops `mapsUrl` — `JSON.stringify` omits an
 * `undefined` value entirely, and an absent key is what a partial update reads
 * as "leave this alone".
 */
export function toBody(draft: TeamDraft) {
  return {
    name: draft.name.trim(),
    city: draft.city.trim() || undefined,
    state: draft.state.trim() || undefined,
    ageGroup: draft.ageGroup,
    classification: draft.classification,
    // The form holds the picker's string; the API wants the pair. `?? undefined`
    // rather than the `null` seasonFromValue returns, which `seasonSchema`
    // would reject outright instead of treating as "not supplied".
    season: seasonFromValue(draft.season) ?? undefined,
  };
}
